import {observable, action} from 'mobx';
import {message} from '@enos/dpl';
import localeText from '@locale';
import Domain from './models/Domain';
import uris from '~/uris';
import Pagination from './Pagination';
import axiosInstance from '~/utils/axiosInstance';

interface IBody {
  forceFresh?: boolean;
  isDelete?: boolean;
}

interface IDomainInfo {
  id: string;
  name: string;
  url: string;
  isAutoImport: boolean;
  description: string;
  roleIds?: string[];
  userGroupIds?: string[];
}

interface IDomainSources {
  createdBy: string;
  createdTime: string;
  description: string;
  id: string;
  name: string;
  isAssignedToDomain: boolean;
}

declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/interface-name-prefix
  export interface AxiosRequestConfig {
    showError?: any;
  }
}

export class DomainStore extends Pagination {
  @observable domains = [];

  @observable currentDomain: IDomainInfo;

  @observable isLoading = false;

  @observable domainsLength: number;

  @observable roles: IDomainSources[];

  @observable initSelectedRoleIds: string[];

  @observable userGroups: IDomainSources[];

  @observable initSelectedUserGroupIds: string[];

  @action.bound
  setRoles(data): void {
    this.roles = data;
  }

  @action.bound
  setInitSelectedRoleIds(data): void {
    const selectedRoles = data.filter(i => i.isAssignedToDomain === true);
    this.initSelectedRoleIds = selectedRoles.map(i => i.id);
  }

  @action.bound
  setUserGroups(data): void {
    this.userGroups = data;
  }

  @action.bound
  setInitSelectedUserGroupIds(data): void {
    const selectedUserGroups = data.filter(i => i.isAssignedToDomain === true);
    this.initSelectedUserGroupIds = selectedUserGroups.map(i => i.id);
  }

  @action
  setDomainsLength(num: number): void {
    this.domainsLength = num;
  }

  @action
  resetLoading(): void {
    this.isLoading = false;
  }

  @action
  setDomains(data): void {
    this.domains = data;
  }

  @action
  changeCurrentDomain(data): void {
    this.currentDomain = data;
  }

  @action.bound
  changePagination(pagination): void {
    super.changePagination(pagination);
    this.getDomainsWithAddition();
  }

  @action.bound
  async getDomains(body = {}): Promise<void> {
    this.isLoading = true;
    await axiosInstance
      .post(uris.getDomains(), {
        ...body
      })
      .then((data: any) => {
        this.setPagination(data.pagination);
        this.setDomains((data.domains || []).map(json => this.updateDomainFromServer(json)));
        this.changeCurrentDomain((data.domains || [])[0]);
        this.setDomainsLength((data.domains || []).length);
        this.resetLoading();
      })
      .catch(() => this.resetLoading());
  }

  @action.bound
  getDomainsWithAddition(body?: IBody): Promise<void> {
    let data = {
      ...this.paginationRequestBody
    };

    if (this.domainsLength === 1 && body && body.isDelete) {
      data = {
        ...this.paginationRequestBodyBefore
      };
    }

    if (body && body.forceFresh) {
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.getDomains({
      ...data,
      ...body
    });
  }

  @action.bound
  getDomainRoleList(id): Promise<void> {
    return axiosInstance
      .get(uris.getDomainRoleList(), {
        params: {id}
      })
      .then((data: any) => {
        this.setRoles(data.roles);
        this.setInitSelectedRoleIds(data.roles);
      });
  }

  @action.bound
  getDomainUserGroupList(id): Promise<void> {
    return axiosInstance
      .get(uris.getDomainUserGroupList(), {
        params: {id}
      })
      .then((data: any) => {
        this.setUserGroups(data.userGroups);
        this.setInitSelectedUserGroupIds(data.userGroups);
      });
  }

  @action.bound
  createDomain(data: IDomainInfo): Promise<void> {
    this.isLoading = true;
    return axiosInstance
      .post(
        uris.createDomain(), {
          name: data.name.trim(),
          url: 'https://'.concat(data.url.trim()),
          description: (data.description || '').trim(),
          isAutoImport: data.isAutoImport,
          roleIds: data?.roleIds,
          userGroupIds: data?.userGroupIds
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_ADD_FAIL')
          }
        }

      )
      .then(() => {
        message.success(localeText('DOMAIN.MESSAGE_ADD_SUCCEED'));
        this.getDomainsWithAddition({forceFresh: true});
      })
      .catch(() => {
        this.resetLoading();
        return Promise.reject();
      });
  }

  @action.bound
  modifyDomain(data: IDomainInfo): Promise<any> {
    this.isLoading = true;
    return axiosInstance
      .post(
        uris.modifyDomain(), {
          id: this.currentDomain.id,
          name: data.name.trim(),
          url: 'https://'.concat(data.url.trim()),
          description: (data.description || '').trim(),
          isAutoImport: data.isAutoImport,
          roleIds: data?.roleIds,
          userGroupIds: data?.userGroupIds
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_MODIFY_FAIL')
          }
        }

      )
      .then((json) => {
        this.updateDomainFromServer(json);
        this.resetLoading();
        message.success(localeText('DOMAIN.MESSAGE_MODIFY_SUCCEED'));
      })
      .catch(() => {
        this.resetLoading();
        return Promise.reject();
      });
  }

  @action.bound
  deleteDomain(data: IDomainInfo): void {
    this.isLoading = true;
    axiosInstance
      .post(
        uris.deleteDomain(), {
          id: data.id
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_DELETE_FAIL')
          }
        }

      )
      .then(() => {
        message.success(localeText('DOMAIN.MESSAGE_DELETE_SUCCESS'));
        this.getDomainsWithAddition({isDelete: true});
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  checkDomainExist(name: string, cb: any): void {
    axiosInstance
      .post(uris.checkNameExist(), null, {
        params: {type: 'domain', name},
        showError: {isShow: false}
      })
      .then((data) => {
        if (!data) {
          cb(false);
        } else {
          cb();
        }
      })
      .catch((error) => {
        cb(error);
      });
  }

  @action.bound
  updateDomainFromServer(json): any {
    let domain = this.domains.find(({id}) => id === json.id);
    if (!domain) {
      domain = new Domain(this, json.id);
    }
    domain.updateFromJson(json);
    return domain;
  }

  @action.bound
  clearState(): void {
    this.initSelectedRoleIds = [];
    this.initSelectedUserGroupIds = [];
  }
}

export default new DomainStore();
